import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { RefreshCw, ShieldCheck, ShieldAlert, Loader2 } from "lucide-react";
import { getAdminDiagnostics } from "@/lib/api/admin-diagnostics.functions";
import { requireAdminRole } from "@/lib/api/admin-users.functions";
import { adminGroups } from "@/lib/admin-nav";

export const Route = createFileRoute("/_authenticated/portal/admin/diagnostics")({
  component: AdminDiagnosticsPage,
});

function AdminDiagnosticsPage() {
  const fetchDiag = useServerFn(getAdminDiagnostics);
  const verifyRole = useServerFn(requireAdminRole);

  const diag = useQuery({
    queryKey: ["admin-diagnostics"],
    queryFn: () => fetchDiag(),
    retry: 1,
  });
  const roleCheck = useQuery({
    queryKey: ["admin-diagnostics-role-check"],
    queryFn: async () => {
      const startedAt = performance.now();
      try {
        await verifyRole();
        return { ok: true, durationMs: Math.round(performance.now() - startedAt), error: null as string | null };
      } catch (e: any) {
        return {
          ok: false,
          durationMs: Math.round(performance.now() - startedAt),
          error: String(e?.message ?? e),
        };
      }
    },
    retry: 0,
  });

  const d = diag.data;
  const totalRoutes = adminGroups.reduce((n, g) => n + g.items.length, 0);

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h2 className="font-display text-2xl text-navy">Admin diagnostics</h2>
          <p className="text-sm text-muted-foreground">
            Confirms your effective role, the server-side role-check result, and
            every admin route the sidebar should expose. Use this if any admin
            page or the sidebar appears to be missing.
          </p>
        </div>
        <button
          type="button"
          onClick={() => {
            diag.refetch();
            roleCheck.refetch();
          }}
          className="inline-flex items-center gap-1 rounded-md border border-input bg-background px-3 py-1.5 text-xs font-medium hover:border-navy"
          disabled={diag.isFetching || roleCheck.isFetching}
        >
          <RefreshCw
            className={`h-3 w-3 ${diag.isFetching || roleCheck.isFetching ? "animate-spin" : ""}`}
          />
          Re-run checks
        </button>
      </div>

      {/* Identity */}
      <section className="rounded-lg border border-border bg-card p-4">
        <h3 className="mb-3 text-sm font-semibold text-navy">Identity</h3>
        {diag.isPending ? (
          <p className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading…
          </p>
        ) : diag.isError ? (
          <p className="text-sm text-red-700">
            Failed: {(diag.error as any)?.message ?? "Unknown error"}
          </p>
        ) : d ? (
          <dl className="grid gap-2 text-sm sm:grid-cols-2">
            <Row label="User ID" value={d.userId} mono />
            <Row label="Email" value={d.email} />
            <Row label="Full name" value={d.fullName} />
            <Row label="Student number" value={d.studentNumber} />
            <Row label="Profile created" value={fmt(d.createdAt)} />
            <Row label="Trial ends" value={fmt(d.trialEndsAt)} />
            <Row
              label="Subscription"
              value={
                d.subscription
                  ? `${d.subscription.plan_code ?? "—"} · ${d.subscription.status ?? "—"}${d.subscription.current_period_end ? ` · until ${fmt(d.subscription.current_period_end)}` : ""}`
                  : "none"
              }
            />
            <Row label="Checked at" value={fmt(d.checkedAt)} />
          </dl>
        ) : null}
      </section>

      {/* Role */}
      <section className="rounded-lg border border-border bg-card p-4">
        <h3 className="mb-3 flex items-center gap-2 text-sm font-semibold text-navy">
          Role check
          {d?.isAdmin ? (
            <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-semibold text-emerald-800">
              <ShieldCheck className="h-3 w-3" /> ADMIN
            </span>
          ) : (
            <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-semibold text-amber-800">
              <ShieldAlert className="h-3 w-3" /> NOT ADMIN
            </span>
          )}
        </h3>
        {d && (
          <dl className="grid gap-2 text-sm sm:grid-cols-2">
            <Row
              label="Roles in user_roles"
              value={d.roles.length ? d.roles.join(", ") : "(none)"}
            />
            <Row label="has_role(_, 'admin') RPC" value={String(d.hasRoleAdmin)} />
            {d.hasRoleError && (
              <Row label="has_role error" value={d.hasRoleError} />
            )}
            <Row label="Diagnostic call duration" value={`${d.durationMs} ms`} />
            <Row
              label="requireAdminRole() server-fn"
              value={
                roleCheck.isPending
                  ? "checking…"
                  : roleCheck.data?.ok
                    ? `OK (${roleCheck.data.durationMs} ms)`
                    : `FAILED — ${roleCheck.data?.error ?? "unknown"}`
              }
            />
            <Row label="JWT email claim" value={d.claims.email} />
            <Row label="JWT aud" value={d.claims.aud} />
          </dl>
        )}
        {!d?.isAdmin && (
          <p className="mt-3 rounded-md bg-amber-50 p-3 text-xs text-amber-900">
            This account is not an admin, so the admin sidebar will not render.
            Sign in with <code>nkadimengdanny7@gmail.com</code> or{" "}
            <code>nkadimengdanny14@gmail.com</code>. The auto-grant trigger will
            promote those emails on next sign-in.
          </p>
        )}
      </section>

      {/* Registry */}
      <section className="rounded-lg border border-border bg-card p-4">
        <h3 className="mb-1 text-sm font-semibold text-navy">
          Admin route registry · {totalRoutes} routes across {adminGroups.length} groups
        </h3>
        <p className="mb-3 text-xs text-muted-foreground">
          This is the exact list the sidebar renders (sourced from{" "}
          <code>src/lib/admin-nav.ts</code>). If a link below works but the
          sidebar item is missing, hard-refresh the browser to drop the cached
          chunk.
        </p>
        <div className="grid gap-4 md:grid-cols-2">
          {adminGroups.map((g) => (
            <div key={g.id} className="rounded-md border border-border/60 p-3">
              <p className="mb-2 text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">
                {g.label} · {g.items.length}
              </p>
              <ul className="space-y-1 text-sm">
                {g.items.map((i) => (
                  <li key={i.to} className="flex items-center justify-between gap-2">
                    <span className="flex items-center gap-2 truncate">
                      <i.icon className="h-3.5 w-3.5 text-muted-foreground" />
                      <span className="truncate">{i.label}</span>
                    </span>
                    <Link
                      to={i.to as any}
                      className="shrink-0 text-[11px] text-blue-700 hover:underline"
                    >
                      open →
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}

function Row({ label, value, mono }: { label: string; value: any; mono?: boolean }) {
  return (
    <div className="flex flex-col">
      <dt className="text-[11px] uppercase tracking-wider text-muted-foreground">{label}</dt>
      <dd className={`text-sm text-foreground ${mono ? "font-mono break-all" : "break-words"}`}>
        {value === null || value === undefined || value === "" ? (
          <span className="text-muted-foreground">—</span>
        ) : (
          String(value)
        )}
      </dd>
    </div>
  );
}

function fmt(iso: string | null | undefined) {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleString("en-ZA");
  } catch {
    return iso;
  }
}
